import { mountVeivoValidationFlow, VeivoValidationFlowHandle } from "@veivo/web-sdk";
import { useEffect, useRef, useState } from "react";
import { Button, Typography } from "@/components/atoms";
import { veivoHandoffUrl } from "@/helpers/veivoHandoffUrl";
import { veivoThemeFromBranding } from "@/helpers/veivoTheme";
import { useOperator } from "@/hooks";
import { useGetVeivoValidationQuery, useStartVeivoValidationMutation } from "@/redux/apis/vehicle/veivoApi";
import { useLazyGetVehicleQuery, useUpdateVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast } from "@/utils";
import { VeivoHelpModal, VeivoHelpTopic } from "../../VeivoHelp/VeivoHelpModal";

interface Step3VeivoVerificationProps {
  vehicleId: string;
  vehicle: Vehicle;
  onNext: () => Promise<void>;
  onBack: () => void;
  onFinishLater: () => void;
}

/**
 * Flag-gated replacement for Step3VehicleDocuments (DEV-1407): mounts the
 * Veivo validation-flow SDK (documents + guided AI walk-around). The wizard
 * only advances when the SERVER-tracked link status reaches `complete` —
 * never on the SDK's client-side callback. Documents land on the vehicle via
 * Veivo's webhook projection, not this form.
 */
export const Step3VeivoVerification = ({ vehicleId, vehicle, onNext, onBack, onFinishLater }: Step3VeivoVerificationProps) => {
  const { branding } = useOperator();
  const hostRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<VeivoValidationFlowHandle | null>(null);
  const advancingRef = useRef(false);
  const [started, setStarted] = useState(false);
  const [startError, setStartError] = useState(false);
  const [helpTopic, setHelpTopic] = useState<VeivoHelpTopic | null>(null);
  // Read through a ref: the SDK keeps the callback it was mounted with, and the
  // wizard hands down a new one per render.
  const onFinishLaterRef = useRef(onFinishLater);
  onFinishLaterRef.current = onFinishLater;

  const [startValidation] = useStartVeivoValidationMutation();
  const [updateVehicle] = useUpdateVehicleMutation();
  const [fetchVehicle] = useLazyGetVehicleQuery();
  const { data: validation } = useGetVeivoValidationQuery(vehicleId, {
    pollingInterval: 4000,
    skip: !started,
  });

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host) return;

    startValidation(vehicleId)
      .unwrap()
      .then((session) => {
        if (cancelled) return;
        flowRef.current = mountVeivoValidationFlow(host, {
          service: { url: session.veivoBaseUrl },
          theme: veivoThemeFromBranding(branding),
          resumeValidationId: session.validationId,
          sessionToken: session.sessionToken,
          // The phone goes to the public verification page (see veivoHandoffUrl),
          // in the operator's colour.
          handoffUrl: (params) => veivoHandoffUrl(params, branding?.primary),
          // Testing affordance, build-time gated: renders a per-step skip control.
      // The service must ALSO have VEIVO_ALLOW_TEST_SKIP on, so this flag alone
      // cannot bypass anything — and it is unset in production builds.
      allowTestSkip: import.meta.env.VITE_VEIVO_ALLOW_TEST_SKIP === "true",
      onError: (err) => console.error("Veivo flow error:", err),
          // The walk-around's ✕ and ? (DEV-1806). On a phone the walk-around
          // is full-screen, so ✕ is the only way back to the wizard: it asks
          // "Finish later?", whose Keep going returns to the camera.
          onClose: () => onFinishLaterRef.current(),
          onHelp: setHelpTopic,
        });
        setStarted(true);
      })
      .catch((err) => {
        console.error("Failed to start Veivo validation:", err);
        setStartError(true);
      });

    return () => {
      cancelled = true;
      void flowRef.current?.dispose();
      flowRef.current = null;
    };
  }, [vehicleId, startValidation, branding]);

  // Server-authoritative completion: advance the wizard once the v2 link row
  // (fed by Veivo webhooks) reports the validation terminal.
  useEffect(() => {
    if (validation?.status !== "complete" || advancingRef.current) return;
    advancingRef.current = true;
    (async () => {
      try {
        // Re-read first: the webhook projection has just added the verified
        // documents, which the wizard's copy of the vehicle predates. The PUT
        // replaces every document it does not list, so it must list those.
        const fresh = await fetchVehicle(vehicleId, false).unwrap();
        await updateVehicle({
          id: vehicleId,
          data: {
            identifier: vehicle.identifier,
            capacity_pax: vehicle.capacity.pax,
            capacity_bags: vehicle.capacity.bags,
            maintenance_fee: vehicle.maintenanceFee,
            ...(vehicle.setupStep === 3 && { setup_step: 4 }),
            documents: fresh.documents.map((d) => ({ uuid: d.uuid, expiry_date: d.expiryDate, document_type: d.documentType })),
            categories: vehicle.categories.map((c) => ({ uuid: c.uuid })),
          },
        }).unwrap();
        await onNext();
      } catch (e) {
        advancingRef.current = false;
        console.error(e);
        addToast("danger", "Verification finished but the wizard could not advance. Please try again.");
      }
    })();
  }, [validation?.status, updateVehicle, fetchVehicle, vehicleId, vehicle, onNext]);

  return (
    <div className="flex flex-col gap-6">
      {startError ? (
        <Typography variant="paragraph" className="text-danger">
          Vehicle verification could not be started. Please try again later or contact support.
        </Typography>
      ) : (
        <div ref={hostRef} className="min-h-[560px] w-full" />
      )}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <Button type="button" variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" onClick={onFinishLater}>
          Finish Later
        </Button>
        <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
          Back
        </Button>
      </div>

      <VeivoHelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />
    </div>
  );
};
